import React, { useEffect, useRef, useState } from "react";
import { AccountLayout } from "./AccountLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { AssetGlyph } from "../Brand";
import api, { ASSET_ICONS } from "../../lib/api";
import { Plus, Wallet as WalletIcon, Copy, QrCode, Camera, X as XIcon } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_ASSETS = [
  { sym: "BTC", networks: ["Bitcoin"] },
  { sym: "ETH", networks: ["Ethereum"] },
  { sym: "USDT", networks: ["Ethereum (ERC-20)", "Tron (TRC-20)", "Polygon", "Arbitrum", "Avalanche C-Chain", "BNB Smart Chain", "Solana"] },
  { sym: "USDC", networks: ["Ethereum (ERC-20)", "Solana", "Polygon", "Base", "Arbitrum", "Optimism", "Avalanche C-Chain", "BNB Smart Chain"] },
  { sym: "SOL", networks: ["Solana"] },
  { sym: "XRP", networks: ["XRP Ledger"] },
  { sym: "ADA", networks: ["Cardano"] },
  { sym: "DOGE", networks: ["Dogecoin"] },
  { sym: "MATIC", networks: ["Polygon"] },
  { sym: "DOT", networks: ["Polkadot"] },
];

// Which network strings are EVM (so MetaMask pull is valid) vs Solana (Phantom valid)
const EVM_NETWORKS = new Set([
  "Ethereum", "Ethereum (ERC-20)", "Polygon", "Arbitrum", "Optimism",
  "Avalanche C-Chain", "BNB Smart Chain", "Base",
]);
const SOL_NETWORKS = new Set(["Solana"]);

export default function Wallets() {
  const [wallets, setWallets] = useState([]);
  const [assets, setAssets] = useState(FALLBACK_ASSETS);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ asset: "USDC", network: "Ethereum (ERC-20)", address: "", label: "" });
  const [saving, setSaving] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  // Drive the asset/network dropdowns from backend so multi-chain stays in sync.
  useEffect(() => {
    api.get("/assets")
      .then((r) => {
        const list = (r.data.assets || []).map((a) => ({ sym: a.symbol, networks: a.networks }));
        if (list.length) setAssets(list);
      })
      .catch(() => { /* fallback already set */ });
  }, []);

  const load = () => {
    setLoading(true);
    api.get("/wallets").then((r) => setWallets(r.data.wallets || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const networks = assets.find((a) => a.sym === form.asset)?.networks || [];
  useEffect(() => {
    if (!networks.includes(form.network)) setForm((f) => ({ ...f, network: networks[0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.asset]);

  const save = async () => {
    if (form.address.length < 10) { toast.error("Address looks too short"); return; }
    if (!form.label.trim()) { toast.error("Add a label"); return; }
    setSaving(true);
    try {
      await api.post("/wallets", form);
      toast.success("Wallet saved");
      setOpen(false);
      setForm({ asset: "USDC", network: "Ethereum (ERC-20)", address: "", label: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const copy = (s) => { navigator.clipboard.writeText(s); toast.success("Address copied"); };

  // ----- Browser-wallet pull -----
  const pullFromMetaMask = async () => {
    if (!window.ethereum) {
      toast.error("MetaMask not detected. Install the extension or open this page in MetaMask's in-app browser.");
      return;
    }
    try {
      const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
      const addr = accounts && accounts[0];
      if (!addr) { toast.error("No account returned from MetaMask"); return; }
      setForm((f) => ({
        ...f,
        address: addr,
        label: f.label || "MetaMask",
        // If the user is on a non-EVM network, switch them to the default EVM one
        network: EVM_NETWORKS.has(f.network) ? f.network : "Ethereum (ERC-20)",
      }));
      toast.success(`Pulled ${addr.slice(0, 6)}…${addr.slice(-4)} from MetaMask`);
    } catch (e) {
      toast.error(e?.message || "MetaMask request was rejected");
    }
  };

  const pullFromPhantom = async () => {
    if (!window.solana || !window.solana.isPhantom) {
      toast.error("Phantom not detected. Install the extension or open this page in Phantom's in-app browser.");
      return;
    }
    try {
      const res = await window.solana.connect();
      const addr = res?.publicKey?.toString();
      if (!addr) { toast.error("No public key returned from Phantom"); return; }
      setForm((f) => ({
        ...f,
        address: addr,
        label: f.label || "Phantom",
        // Solana-only chain; force network to Solana
        asset: SOL_NETWORKS.has(f.network) ? f.asset : (f.asset === "USDC" ? "USDC" : f.asset === "USDT" ? "USDT" : "SOL"),
        network: "Solana",
      }));
      toast.success(`Pulled ${addr.slice(0, 6)}…${addr.slice(-4)} from Phantom`);
    } catch (e) {
      toast.error(e?.message || "Phantom request was rejected");
    }
  };

  const onQrDetected = React.useCallback((text) => {
    // Many wallets emit a URI scheme — strip prefixes like `ethereum:`, `bitcoin:`, `solana:`
    let addr = String(text || "").trim();
    addr = addr.replace(/^(ethereum|bitcoin|solana|polygon|arbitrum|tron|litecoin):/i, "");
    // Strip any query string (e.g. `?amount=…`)
    addr = addr.split("?")[0];
    if (addr.length < 10) {
      toast.error("Scanned value doesn't look like a wallet address");
      return;
    }
    setForm((f) => ({ ...f, address: addr }));
    setScanOpen(false);
    toast.success(`Scanned ${addr.slice(0, 6)}…${addr.slice(-4)}`);
  }, []);

  return (
    <AccountLayout
      title="Wallet address book"
      subtitle="Save your wallets so future orders are one-click."
      actions={
        <Button onClick={() => setOpen(true)} className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5" data-testid="add-wallet-btn">
          <Plus className="w-4 h-4" /> Add address
        </Button>
      }
    >
      <div className="xch-card overflow-hidden" data-testid="wallets-card">
        {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && wallets.length === 0 && (
          <div className="p-12 text-center space-y-3" data-testid="wallets-empty">
            <WalletIcon className="w-10 h-10 mx-auto text-muted-foreground/40" />
            <div className="font-display text-lg font-bold text-foreground">No saved wallets yet</div>
            <div className="text-sm text-muted-foreground">Add an address you trust — we'll use it for future orders.</div>
            <Button onClick={() => setOpen(true)} className="bg-primary hover:bg-primary/90 text-white rounded-xl mt-2">
              Add your first wallet
            </Button>
          </div>
        )}
        {!loading && wallets.length > 0 && (
          <div className="divide-y divide-border" data-testid="wallets-list">
            {wallets.map((w) => (
              <div key={w.id} className="p-4 flex items-center gap-3" data-testid={`wallet-${w.id}`}>
                <AssetGlyph symbol={w.asset} color={ASSET_ICONS[w.asset] || "#4F46FF"} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-foreground flex items-center gap-2 flex-wrap">
                    {w.label}
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{w.network}</span>
                    <WalletStatusPill status={w.status} />
                  </div>
                  <button onClick={() => copy(w.address)} className="font-mono text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1" data-testid={`copy-${w.id}`}>
                    <span className="truncate max-w-[280px] sm:max-w-md">{w.address}</span>
                    <Copy className="w-3 h-3 shrink-0" />
                  </button>
                </div>
                <div className="text-[11px] text-muted-foreground tabular-nums shrink-0">
                  {new Date(w.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="add-wallet-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add wallet address</DialogTitle>
            <DialogDescription className="text-xs">
              Paste your address, scan a QR code, or pull it directly from your browser wallet — typos lose stablecoins forever.
            </DialogDescription>
          </DialogHeader>

          {/* High-trust ingest helpers — top of dialog */}
          <div className="grid grid-cols-3 gap-2 mt-1" data-testid="wallet-ingest-helpers">
            <Button type="button" variant="outline" size="sm"
                    onClick={pullFromMetaMask}
                    className="rounded-xl gap-1.5 text-xs h-9"
                    data-testid="wallet-pull-metamask-btn">
              <MetaMaskFox /> MetaMask
            </Button>
            <Button type="button" variant="outline" size="sm"
                    onClick={pullFromPhantom}
                    className="rounded-xl gap-1.5 text-xs h-9"
                    data-testid="wallet-pull-phantom-btn">
              <PhantomGhost /> Phantom
            </Button>
            <Button type="button" variant="outline" size="sm"
                    onClick={() => setScanOpen(true)}
                    className="rounded-xl gap-1.5 text-xs h-9"
                    data-testid="wallet-scan-qr-btn">
              <QrCode className="w-3.5 h-3.5" /> Scan QR
            </Button>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1 -mb-1" data-testid="wallet-ingest-helpers-hint">
            Pulled or scanned address shows up below — review the network before saving.
          </div>

          <div className="space-y-3 mt-2">
            <Field label="Asset">
              <Select value={form.asset} onValueChange={(v) => setForm({ ...form, asset: v })}>
                <SelectTrigger className="rounded-xl" data-testid="wallet-asset-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {assets.map((a) => <SelectItem key={a.sym} value={a.sym}>{a.sym}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Network">
              <Select value={form.network} onValueChange={(v) => setForm({ ...form, network: v })}>
                <SelectTrigger className="rounded-xl" data-testid="wallet-network-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {networks.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Wallet address">
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                     placeholder="bc1q…" className="rounded-xl font-mono text-xs" data-testid="wallet-address-input" />
            </Field>
            <Field label="Label">
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                     placeholder="e.g. Ledger main" className="rounded-xl" data-testid="wallet-label-input" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-white rounded-xl" data-testid="wallet-save-btn">
              {saving ? "Saving…" : "Save wallet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QrScannerDialog open={scanOpen} onOpenChange={setScanOpen} onDetected={onQrDetected} />
    </AccountLayout>
  );
}

// =========================
// QR scanner (uses BarcodeDetector when available, falls back to manual paste)
// =========================
function QrScannerDialog({ open, onOpenChange, onDetected }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [supported, setSupported] = useState(null);   // null = unknown, true/false
  const [error, setError] = useState(null);
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (!open) return;
    setError(null);
    const hasDetector = typeof window !== "undefined" && "BarcodeDetector" in window;
    setSupported(hasDetector);
    if (!hasDetector) return;
    let cancelled = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play();
        const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        const tick = async () => {
          if (cancelled) return;
          try {
            const codes = await detector.detect(v);
            if (codes && codes[0]?.rawValue) {
              onDetected(codes[0].rawValue);
              return; // stop the loop
            }
          } catch (_) { /* ignore per-frame errors */ }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        setError(e?.message || "Camera access denied");
      }
    })();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open, onDetected]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <QrCode className="w-5 h-5" /> Scan wallet QR
          </DialogTitle>
          <DialogDescription className="text-xs">
            Point your camera at a wallet QR code. We'll extract the address automatically.
          </DialogDescription>
        </DialogHeader>

        {supported === true && !error && (
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-square" data-testid="qr-scanner-video-wrap">
            <video ref={videoRef} muted playsInline className="w-full h-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-2/3 aspect-square border-2 border-white/80 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
            </div>
            <div className="absolute top-2 left-2 right-2 text-[11px] text-white/90 bg-black/40 rounded-xl px-2 py-1 text-center">
              Frame the QR code inside the square
            </div>
          </div>
        )}

        {supported === false && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="qr-scanner-unsupported">
            <Camera className="w-4 h-4 inline mr-1.5" />
            Your browser doesn't support in-page QR scanning. Paste the address below, or open this page in Chrome or a mobile browser.
          </div>
        )}

        {error && (
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900" data-testid="qr-scanner-error">
            <XIcon className="w-4 h-4 inline mr-1.5" />
            {error} — paste the address below instead.
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            Or paste the address
          </Label>
          <div className="flex gap-2">
            <Input value={manual} onChange={(e) => setManual(e.target.value)}
                   placeholder="bc1q… / 0x… / Solana base58"
                   className="rounded-xl font-mono text-xs" data-testid="qr-scanner-manual-input" />
            <Button onClick={() => { if (manual.trim().length >= 10) onDetected(manual.trim()); else toast.error("Address looks too short"); }}
                    disabled={manual.length < 10}
                    className="bg-primary hover:bg-primary/90 text-white rounded-xl"
                    data-testid="qr-scanner-manual-use-btn">
              Use
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const WalletStatusPill = ({ status }) => {
  if (!status || status === "approved")
    return <span className="text-[9px] uppercase tracking-wider font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full" data-testid="wallet-status-approved">Approved</span>;
  if (status === "pending")
    return <span className="text-[9px] uppercase tracking-wider font-bold bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full" data-testid="wallet-status-pending">Pending review</span>;
  if (status === "rejected")
    return <span className="text-[9px] uppercase tracking-wider font-bold bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-full" data-testid="wallet-status-rejected">Rejected</span>;
  return null;
};

// Tiny inline SVGs so we don't pull in a logo asset
const MetaMaskFox = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path d="M27 4 17.5 11l1.7-4L27 4Z" fill="#E2761B"/>
    <path d="M5 4l9.4 7.1L12.8 7 5 4Z" fill="#E4761B"/>
    <path d="M23.3 21.2 21 25l5 1.4 1.4-4.8-4.1-.4ZM4.6 21.6 6 26.4 11 25l-2.3-3.8-4.1.4Z" fill="#E4761B"/>
    <path d="m10.7 14.3-1.4 2.1 5 .2-.2-5.4-3.4 3.1ZM21.3 14.3l-3.5-3.1-.1 5.4 5-.2-1.4-2.1Z" fill="#E4761B"/>
    <path d="M11 25l3-1.5-2.6-2L11 25ZM18 23.5l3 1.5-.4-3.5-2.6 2Z" fill="#D7C1B3"/>
  </svg>
);

const PhantomGhost = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 128 128" fill="none" aria-hidden="true">
    <rect width="128" height="128" rx="64" fill="#AB9FF2"/>
    <path d="M110 64.9c0-25.2-20.4-45.7-45.6-45.7S18.9 39.7 18.9 64.9c0 13.7 6.1 26 15.7 34.3l.1.1c.2.2.4.4.6.5 2.2 2.2 4.7 4.1 7.4 5.7l.6.3.4.2c1.3.7 2.7 1.3 4 1.9 8.6 3.5 18 4.5 27.1 3 1.3-.2 2.7-.4 4-.7 1.3-.3 2.6-.7 3.9-1.1 1.3-.4 2.6-.8 3.8-1.3l.4-.2.6-.3c1.5-.6 3-1.3 4.5-2.1l.4-.2c1.5-.8 2.9-1.7 4.3-2.6 1.4-1 2.7-2.1 4-3.2.1-.1.2-.2.3-.3.1-.1.2-.2.3-.3 8.5-8.2 13.7-19.6 13.7-32Z" fill="#FFFEFE"/>
    <path d="M85.8 56.4c-2.9 0-5.3 2.4-5.3 5.4s2.4 5.4 5.3 5.4 5.3-2.4 5.3-5.4-2.4-5.4-5.3-5.4ZM44.6 56.4c-2.9 0-5.3 2.4-5.3 5.4s2.4 5.4 5.3 5.4S50 64.7 50 61.7s-2.4-5.3-5.4-5.3Z" fill="#534BB1"/>
  </svg>
);
